<app-app-bar></app-app-bar>

<app-main-content>
  <div class="lens-page lens-dashboard">
    @if (!hasTestResults() && recentTestsLoading()) {
    <section class="dash-hero lens-banner" aria-busy="true" aria-label="Loading your internet quality">
      <div><p class="dash-eyebrow">Your internet</p><h1>Your Internet Quality</h1>
        <p class="dash-hero__desc" role="status">Loading your latest result…</p></div>
    </section>
    <div class="dash-loading-metrics" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
    } @else if (!hasTestResults()) {
    <section
      class="dash-hero dash-hero--welcome lens-banner"
      aria-labelledby="welcome-heading"
    >
      <div class="dash-container dash-hero__grid">
        <div class="dash-hero__copy">
          <p class="dash-eyebrow">LENS · Campus Connection</p>
          <h1 id="welcome-heading" class="dash-hero__title">
            Your campus network dashboard
          </h1>
          <p class="dash-hero__desc">
            Check connection quality, discover the best study spots, and help
            CTBE improve the network you rely on every day at Addis Ababa
            University.
          </p>

          @if (isRetesting()) {
          <div class="dash-hero__test-status" aria-live="polite">
            <span>{{ testPhase() }}</span
            ><span>{{ progress() }}%</span>
          </div>
          <div
            class="dash-hero__test-progress"
            role="progressbar"
            [attr.aria-valuenow]="progress()"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Test in progress"
          >
            <span [style.width.%]="progress()"></span>
          </div>
          } @else if (testError()) {
          <p class="dash-hero__test-error" role="alert">{{ testError() }}</p>
          }
        </div>
          <div class="dash-hero__actions">
            <button
              type="button"
              class="dash-btn dash-btn--primary"
              [disabled]="isRetesting()"
              (click)="runTest()"
            >
              <mat-icon [class.dash-btn__spin]="isRetesting()">{{
                isRetesting() ? "refresh" : "play_arrow"
              }}</mat-icon>
              {{ testButtonLabel() }}
            </button>
          </div>

        <div
          class="dash-hero__metrics dash-hero__metrics--empty"
          aria-hidden="true"
        >
          <span class="dash-hero__metrics-label">Network quality score</span>
          <span class="dash-hero__metrics-score dash-hero__metrics-score--empty"
            >—</span
          >
          <p class="dash-hero__metrics-hint">
            Run your first test to see live speed, latency, and quality ratings.
          </p>
        </div>
      </div>
    </section>

    <section
      class="dash-container dash-features"
      aria-label="Platform features"
    >
      <div class="dash-features__grid">
        <article class="dash-feature">
          <mat-icon>wifi</mat-icon>
          <h2>Know your connection</h2>
          <p>Instant quality checks wherever you are on campus.</p>
        </article>
        <article class="dash-feature">
          <mat-icon>place</mat-icon>
          <h2>Find your best spot</h2>
          <p>Recommendations for study, class, and meetings.</p>
        </article>
        <article class="dash-feature">
          <mat-icon>campaign</mat-icon>
          <h2>Stay informed</h2>
          <p>Campus notices and network status updates.</p>
        </article>
      </div>
    </section>
    } @else {
    <!-- Hero: full-width band, no card wrapper -->
    <section class="dash-hero lens-banner" aria-labelledby="hero-heading">
      <div class="dash-container dash-hero__grid">
        <div class="dash-hero__copy">
          <p class="dash-eyebrow">Your internet</p>
          <h1 id="hero-heading" class="dash-hero__title">
            Your Internet Quality
          </h1>
          <p class="dash-hero__desc">
            A live snapshot of your internet and spot quality for everyday
            activities.
          </p>
          <p class="dash-hero__meta"><mat-icon aria-hidden="true">schedule</mat-icon>Last updated {{ quality().updatedAgo }}</p>



          @if (isRetesting()) {
          <div class="dash-hero__test-status" aria-live="polite">
            <span>{{ testPhase() }}</span
            ><span>{{ progress() }}%</span>
          </div>
          <div
            class="dash-hero__test-progress"
            role="progressbar"
            [attr.aria-valuenow]="progress()"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Test in progress"
          >
            <span [style.width.%]="progress()"></span>
          </div>
          } @else if (testError()) {
          <p class="dash-hero__test-error" role="alert">{{ testError() }}</p>
          }
        </div>
          <div class="dash-hero__actions">
            <button
              type="button"
              class="dash-btn dash-btn--primary"
              [disabled]="isRetesting()"
              (click)="runTest()"
            >
              <mat-icon [class.dash-btn__spin]="isRetesting()"
                >refresh</mat-icon
              >
              {{ testButtonLabel() }}
            </button>
          </div>


      </div>
    </section>

    <!-- KPI cards only -->
    <section class="dash-container dash-kpis" aria-label="Connection metrics">
      <article class="dash-quality">
        <div class="dash-quality__ring" role="img" [attr.aria-label]="'Quality score: ' + quality().score + ' out of 100'">
          <svg viewBox="0 0 88 88" aria-hidden="true"><circle class="dash-quality__track" cx="44" cy="44" r="38"/>
            <circle class="dash-quality__arc" cx="44" cy="44" r="38" [style.stroke]="quality().color" stroke-dasharray="238.76" [attr.stroke-dashoffset]="238.76 * (1 - quality().score / 100)"/></svg>
          <b>{{ quality().score }}</b>
        </div>
        <div><span class="dash-kpi__label">Quality score</span>
          <span class="lens-badge" [class]="'lens-badge--' + qualityClass(quality().label)">{{ quality().label }}</span></div>
      </article>
      <article class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">Download</span>
          <mat-icon aria-hidden="true">south</mat-icon>
        </div>
        <p class="dash-kpi__value">
          {{ quality().download }}<small>Mbps</small>
        </p>
      </article>
      <article class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">Upload</span>
          <mat-icon aria-hidden="true">north</mat-icon>
        </div>
        <p class="dash-kpi__value">{{ quality().upload }}<small>Mbps</small></p>
      </article>
      <article class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">Latency</span>
          <mat-icon aria-hidden="true">speed</mat-icon>
        </div>
        <p class="dash-kpi__value">
          {{ quality().latency ?? "—" }}@if (quality().latency != null)
          {<small>ms</small>}
        </p>
      </article>
    </section>
    <ul class="dash-hero__tags" aria-label="Quality by activity">
      @for (item of activityRatings(); track item.activity) {
        <li><span [style.background]="item.color"></span>{{ item.rating }} for {{ item.activity }}</li>
      }
    </ul>
    }

    <!-- Best Spots -->
    <section class="dash-container dash-spots" aria-labelledby="spots-heading">
      <header class="dash-section-head">
        <div>
          <h2 id="spots-heading" class="dash-section-head__title">
            Best Spots
          </h2>
          <p class="dash-section-head__subtitle">
            Top spots ranked by internet quality
          </p>
        </div>
        <div class="dash-chips" role="tablist" aria-label="Spot categories">
          @for (cat of categoryFilters; track cat.id) {
          <button
            type="button"
            class="dash-chip"
            [class.dash-chip--active]="selectedCategory() === cat.id"
            role="tab"
            [attr.aria-selected]="selectedCategory() === cat.id"
            (click)="selectCategory(cat.id)"
          >
            {{ cat.label }}
          </button>
          }
        </div>
      </header>

      <div class="dash-spots__layout">
        @if (spotsLoading()) {
        <p class="dash-spots__message" aria-live="polite">
          Loading recommended spots…
        </p>
        } @else if (spotsError()) {
        <p class="dash-spots__message" role="alert">
          Recommended spots are temporarily unavailable.
        </p>
        } @else if (!filteredBestSpots().length) {
        <p class="dash-spots__message">
          No recommendations are available for this activity yet.
        </p>
        } @else {
        <div class="dash-spots__grid">
          @for (spot of filteredBestSpots(); track spot.id) {
          <a class="dash-spot" [routerLink]="routeLink(eRoutes.SPOTS)">
            <div class="dash-spot__top">
              <span class="dash-spot__category">{{ spot.label }}</span>
              <span
                class="dash-spot__badge"
                [class]="'dash-spot__badge--' + qualityClass(spot.quality)"
              >
                {{ spot.quality }}
              </span>
            </div>
            <h3 class="dash-spot__name">{{ spot.place }}</h3>
            <p class="dash-spot__desc">{{ spot.description }}</p>
          </a>
          }
        </div>
        }
      </div>
    </section>

    <!-- Announcements: own section, news panel style -->
    <section class="dash-container dash-news" aria-labelledby="news-heading">
      <header class="dash-section-head dash-section-head--compact">
        <div>
          <h2 id="news-heading" class="dash-section-head__title">
            Announcements
          </h2>
          <p class="dash-section-head__subtitle">Campus and network updates</p>
        </div>
      </header>

      @if (announcementsLoading()) {
      <p class="dash-spots__message">Loading announcements…</p>
      } @else if (announcementsError()) {
      <div class="dash-spots__message" role="alert">
        Announcements could not be loaded.
        <button
          type="button"
          class="dash-news__retry"
          (click)="loadAnnouncements()"
        >
          Try again
        </button>
      </div>
      } @else if (!announcements().length) {
      <p class="dash-spots__message">There are no current announcements.</p>
      } @else {
      <ul class="dash-news__list">
        @for (notice of announcements(); track notice.id) {
        <li class="dash-news__item">
          <a class="dash-news__title-link" [routerLink]="routeLink(eRoutes.HUB)">
            <span class="dash-news__marker" aria-hidden="true"><mat-icon>{{ announcementIcon(notice) }}</mat-icon></span>
            <h3 class="dash-news__title">{{ notice.title }}</h3>
          </a>
        </li>
        }
      </ul>
      <a
        class="dash-link-btn dash-news__all"
        [routerLink]="routeLink(eRoutes.HUB)"
        >View all announcements<mat-icon>arrow_forward</mat-icon></a
      >
      }
    </section>

    <!-- Recent activity -->
    <section
      class="dash-container dash-activity"
      aria-labelledby="activity-heading"
    >
      <header class="dash-section-head dash-section-head--compact">
        <div>
          <h2 id="activity-heading" class="dash-section-head__title">
            Last test
          </h2>
          <p class="dash-section-head__subtitle">
            Your latest completed network test
          </p>
        </div>
        <a class="dash-link-btn" [routerLink]="routeLink(eRoutes.INSIGHT)">
          View history
          <mat-icon>arrow_forward</mat-icon>
        </a>
      </header>

      @if (recentTestsLoading()) {
      <div class="dash-activity__empty" aria-live="polite">
        <span class="dash-activity__spinner"></span>
        Loading your latest tests…
      </div>
      } @else if (recentTestsError()) {
      <div
        class="dash-activity__empty dash-activity__empty--error"
        role="alert"
      >
        <mat-icon>cloud_off</mat-icon>
        Your latest tests could not be loaded.
      </div>
      } @else if (recentTests().length) {
      <div class="dash-activity__list">
        @for (test of recentTests(); track test.test_id) {
        <article class="dash-activity__item">
          <div class="dash-activity__place">
            <span class="dash-activity__icon"><mat-icon>wifi</mat-icon></span>
            <div>
              <h3>{{ test.spot_name || "Campus network test" }}</h3>
              <p>
                {{ test.location_area || "Location not recorded" }} ·
                {{ test.relative_timestamp }}
              </p>
            </div>
          </div>
          <div class="dash-activity__metrics">
            <span
              ><small>Download</small
              ><strong>{{
                test.download_speed == null
                  ? "—"
                  : (test.download_speed | number : "1.1-1")
              }}</strong>
              Mbps</span
            >
            <span
              ><small>Upload</small
              ><strong>{{
                test.upload_speed == null
                  ? "—"
                  : (test.upload_speed | number : "1.1-1")
              }}</strong>
              Mbps</span
            >
            <span class="lens-badge" [class.lens-badge--excellent]="test.qoe_classification === 'Excellent'" [class.lens-badge--good]="test.qoe_classification === 'Good'" [class.lens-badge--fair]="test.qoe_classification === 'Fair'" [class.lens-badge--poor]="test.qoe_classification === 'Poor'">{{ test.qoe_classification || "Low data" }}</span>
          </div>
        </article>
        }
      </div>
      } @else {
      <div class="dash-activity__empty">
        <mat-icon>history</mat-icon>
        <span
          >No completed tests yet. Run a test and your latest result will appear
          here.</span
        >
      </div>
      }
    </section>
  </div>
</app-main-content>
